import 'leaflet-editable'
import 'leaflet.path.drag'
import 'leaflet/dist/leaflet.css'
import { useGeoCoder } from 'models/mapLayer'
import { getMapBounds } from 'helpers/getters'
import { useZoom } from 'models/publicTransport'
import React, { useEffect, useMemo } from 'react'

import { NewMap } from 'compositions/map'

import EditableMapContent from '../EditableMapContent/EditableMapContent'

import useLeafletExtensions from '../NewMap/util/useLeafletExtensions'

import useEditableMap from './util/useEditableMap'


const EditableMap: React.FC<EditableMap.MapProps> = (props) => {
  const {
    className,
    form,
    center,
    withAddress,
    locateUser,
    fixedPlacemarks,
    withFullScreen = true,
  } = props

  const { isLoaded: isExtensionsLoaded } = useLeafletExtensions()

  const {
    placemarks,
    placemarksField,
    polylines,
    polylinesField,
    multiPolygons,
    multiPolygonsField,
  } = useEditableMap(form)

  useEffect(() => {
    if (withAddress && !form.fields.address) {
      console.error('EditableMap: withAddress is true but form.fields.address is not provided')
    }
  }, [ withAddress, form ])

  useGeoCoder({
    addressField: form.fields.address,
    placemarksField: form.fields.placemarks,
  }, !withAddress)

  const multiPolygonsForBounds = useMemo(() => multiPolygons.map(({ geometry }) => ({ geometries: geometry.flat(1) })), [ multiPolygons ])
  // convert multiPolygons to match MapContentProps['multiPolygons'] type which is used in getMapBounds()
  const bounds = useMemo(() => getMapBounds({
    placemarks,
    polylines,
    multiPolygons: multiPolygonsForBounds,
  }), [ multiPolygonsForBounds, placemarks, polylines ])

  const isSinglePlacemark = !polylines?.length && !multiPolygons?.length && placemarks?.length === 1
  const initialCenter = useMemo(() => isSinglePlacemark ? placemarks[0].geometry : center, [ center, isSinglePlacemark, placemarks ])
  const initialZoom = 13
  const { zoom, center: zoomCenter, updateZoom, updateCenter } = useZoom(initialZoom, initialCenter)

  if (!isExtensionsLoaded) {
    return <></>
  }

  return (
    <NewMap
      className={className}
      center={zoomCenter}
      locateUser={locateUser}
      addressField={withAddress ? form.fields.address : null}
      whenCreated={(map) => bounds?.length && map.fitBounds(bounds, { maxZoom: initialZoom })}
      withFullScreen={withFullScreen}
      zoom={zoom}
      onZoomEnd={({ zoom, center }) => {
        updateZoom(zoom)
        updateCenter(center)
      }}
    >
      <EditableMapContent
        placemarks={placemarks}
        fixedPlacemarks={fixedPlacemarks}
        polylines={polylines}
        multiPolygons={multiPolygons}
        placemarksField={placemarksField}
        polylinesField={polylinesField}
        multiPolygonsField={multiPolygonsField}
      />
    </NewMap>
  )
}


export default EditableMap
